import './ListItemActions.scss'

import React, { useState } from 'react'
import { useDispatch } from 'react-redux'

import { AddToCoursepack, AddToList, openAddToCoursepackModal, openAddToListModal } from 'hb4e-ui-context'
import { Icon, Popover, Modal, useConfirm } from 'hb4e-ui-core-ui'
import { HasPermission } from 'hb4e-ui-authentication'
import { CoreConstants } from 'hb4e-ui-core'
import { ProductService } from 'hb4e-ui-product'
import { useFeatureFlags } from 'hb4e-ui-env-config'

import { removeListItem, addListProductToCart, fetchLists, fetchAdditionalLists } from '../../../../../../../actions'
import { getProductDetailsForListItem } from '../../../../../../../list-service'

const { PERM_ADD_TO_COURSE, PERM_ADD_TO_PERSONAL_CART } = CoreConstants
const { isFutureProduct } = ProductService

const ListItemActions = ({ item, list }) => {
  // TODO: KT(10.12.2024) - Remove feature flag as part of heet-688-show-add-to-modals clean up
  const { heet688ShowAddToModals } = useFeatureFlags()

  const dispatch = useDispatch()
  const [isActionsDropdownOpen, setIsActionsDropdownOpen] = useState(false)
  const [isAddToCoursepackModalOpen, setIsAddToCoursepackModalOpen] = useState(false)
  const [isCopyToListModalOpen, setIsCopyToListModalOpen] = useState(false)

  const { shouldHideProductAddToCart, shouldHideAddToCoursepack, isExpired, productTitle } =
    getProductDetailsForListItem(item)

  const { product } = item

  const { confirm: confirmRemoveItem } = useConfirm({
    title: 'Remove Item',
    prompt: 'Are you sure you’d like to remove this item?',
    cancelLabel: 'Cancel',
    confirmLabel: 'Yes, Remove Item',
  })

  const handleAddToCoursepackButtonClick = () => {
    setIsActionsDropdownOpen(false)

    // TODO: KT(10.12.2024) - Remove feature flag as part of heet-688-show-add-to-modals clean up
    if (heet688ShowAddToModals) {
      dispatch(openAddToCoursepackModal({ product: item, shouldShowProductOptions: true }))
    } else {
      setIsAddToCoursepackModalOpen(true)
    }
  }

  const handleCopyToListButtonClick = () => {
    setIsActionsDropdownOpen(false)

    // TODO: KT(10.12.2024) - Remove feature flag as part of heet-688-show-add-to-modals clean up
    if (heet688ShowAddToModals) {
      dispatch(
        openAddToListModal({
          product,
          modalTitle: 'Copy to another List',
          onSuccess: () => dispatch(fetchLists()),
        }),
      )
    } else {
      setIsCopyToListModalOpen(true)
    }
  }

  const handleAddToCartClick = async () => {
    setIsActionsDropdownOpen(false)
    await dispatch(addListProductToCart(product))
  }

  const handleRemoveItemButtonClick = async () => {
    setIsActionsDropdownOpen(false)

    if (await confirmRemoveItem()) {
      await dispatch(removeListItem(list.getServerID(), item.id))
    }
  }

  const handleModalClose = () => {
    setIsAddToCoursepackModalOpen(false)
    setIsCopyToListModalOpen(false)
  }

  const renderModal = () => {
    const isModalOpen = isAddToCoursepackModalOpen || isCopyToListModalOpen
    let modalTitle = ''
    let formToRender = null

    if (isAddToCoursepackModalOpen) {
      modalTitle = 'Add to Coursepack'
      formToRender = (
        <AddToCoursepack product={item} shouldShowProductOptions={true} onSuccess={handleModalClose} isInModal={true} />
      )
    } else if (isCopyToListModalOpen) {
      modalTitle = 'Copy to another List'
      formToRender = (
        <AddToList
          isInModal={true}
          product={item.product}
          onSuccess={({ productWasAdded }) => {
            handleModalClose()

            if (productWasAdded) {
              dispatch(fetchLists())
            }
          }}
        />
      )
    }

    return (
      <Modal
        title={modalTitle}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        className="list-item-details__actions-modal"
      >
        {formToRender}
      </Modal>
    )
  }

  const renderAddToCoursepackButton = () => {
    const isDisabled = item === null || isFutureProduct(item)

    if (shouldHideAddToCoursepack) {
      return null
    }

    return (
      <HasPermission permission={[PERM_ADD_TO_COURSE]}>
        <li className="list-item-actions__list-item">
          <button
            className="list-item-actions__list-item-button"
            onClick={handleAddToCoursepackButtonClick}
            disabled={isDisabled}
          >
            Add to a Coursepack
          </button>
        </li>
      </HasPermission>
    )
  }

  const renderRemoveItemButton = () => {
    if (list.isFreeTrialsList() && !isExpired) {
      return null
    }

    return (
      <li className="list-item-actions__list-item">
        <button className="list-item-actions__list-item-button" onClick={handleRemoveItemButtonClick}>
          Remove Item
        </button>
      </li>
    )
  }

  return (
    <div className="list-item-actions">
      <Popover
        placement={Popover.Placement.BOTTOM}
        isOpen={isActionsDropdownOpen}
        onClose={() => setIsActionsDropdownOpen(false)}
      >
        <Popover.Target>
          <button
            aria-label={`${productTitle}, list item actions`}
            onClick={() => setIsActionsDropdownOpen(!isActionsDropdownOpen)}
            aria-expanded={isActionsDropdownOpen}
            aria-controls="list-item-actions__actions-list"
            data-testid="list-items-actions-open-button"
          >
            <Icon.CircleChevronDownSolid
              className="list-item-actions__dropdown-icon"
              rotation={isActionsDropdownOpen ? [Icon.Effect.ROTATE_180] : 0}
            />
          </button>
        </Popover.Target>

        <Popover.Content>
          <div className="list-item-actions__dropdown-container">
            <ul id="list-item-actions__actions-list" className="list-item-actions__dropdown-list">
              {renderAddToCoursepackButton()}
              <li className="list-item-actions__list-item">
                <button className="list-item-actions__list-item-button" onClick={handleCopyToListButtonClick}>
                  Copy to another List
                </button>
              </li>
              {!shouldHideProductAddToCart && list.isSavedItemsList() && (
                <HasPermission permission={[PERM_ADD_TO_PERSONAL_CART]}>
                  <li className="list-item-actions__list-item">
                    <button className="list-item-actions__list-item-button" onClick={handleAddToCartClick}>
                      Add to Cart
                    </button>
                  </li>
                </HasPermission>
              )}
              {renderRemoveItemButton()}
            </ul>
          </div>
        </Popover.Content>
      </Popover>

      {renderModal()}
    </div>
  )
}

export default ListItemActions
